import type { CSSProperties, ReactNode } from "react";
import { CARD_FILL } from "./card-frame";
import { pewterFrame, pewterInner, solid } from "./pewter-edge";

/**
 * The category pill, built like the app's
 * (`apps/mobile/src/shared/ui/cards/category-card.tsx`) — owner, 2026-09-07:
 * "let's do them like the mobile app ones", on the home rail and the hub rail
 * alike. One component so the two cannot drift apart again; they were two hand
 * copies at different sizes, with different discs and different shadows.
 *
 * It wears the pewter band even though it is tappable, and the
 * no-band-on-controls rule does not bite: a chip is a control, but this is a
 * CARD you scroll a rail of — the same shape as the tiles under it — that
 * happens to be tappable. On the band it reads as an object beside those cards
 * rather than a chip floating over them.
 *
 * The clay art keeps its disc. It is lit for white, and the disc is what holds
 * it down on the pill.
 *
 * 3px band, not the panel's 4: on a 50px pill a 4px band is a stripe rather
 * than an edge. The app's number, for the app's reason.
 */

const GAP = 3;
const R = 999;

/**
 * `topLit`, not the `sheen` the See-all pill and the arrows took (owner,
 * 2026-09-13). The two styles are picked by what the thing IS, not by size:
 * sheen's single corner sweep suits one control sitting alone in a header,
 * while a rail of these is a repeating grid of small cards, the case topLit
 * is specified for, and what `city-filter-slider` already wears beside them.
 * A sweep repeated eight times across a row reads as eight different lights.
 */
export const CATEGORY_PILL_FRAME = pewterFrame(R, { gap: GAP, style: "topLit" });
const INNER = pewterInner(CARD_FILL, R, { gap: GAP });
/** Chosen: the surface inside the frame goes navy, the band stays. */
const INNER_ACTIVE = pewterInner(solid("#110E39"), R, { gap: GAP, hairline: "rgba(17,14,57,0.5)" });


/**
 * The app's proportions at `md`. `lg` is the same object sized for the home
 * rail; `sm` is it sized for the sticky bar, where the rail is furniture
 * riding over the list rather than the thing you came to look at — at `md` it
 * ate a third of a phone screen (owner, 2026-09-07).
 */
const SIZES = {
  sm: { pill: "min-h-[40px] gap-1.5 py-1 pe-3.5 ps-1.5", disc: "h-8 w-8", art: "h-[22px] w-[22px]", text: "text-[12.5px] leading-[17px]" },
  md: { pill: "min-h-[50px] gap-2 py-1.5 pe-4 ps-2", disc: "h-10 w-10", art: "h-7 w-7", text: "text-[13.5px] leading-[19px]" },
  lg: { pill: "min-h-[62px] gap-2.5 py-2 pe-5 ps-2.5", disc: "h-12 w-12", art: "h-8 w-8", text: "text-[15px] leading-[21px]" },
} as const;

export type CategoryPillSize = keyof typeof SIZES;

/**
 * The outer frame style. No drop shadow, resting or chosen (owner,
 * 2026-10-11): chips and pills never cast one, and in these sideways rails it
 * was sliced flat into a grey block anyway. Chosen shows as the navy inside.
 * The arguments stay so callers need not change.
 */
export function categoryPillFrameStyle(_active?: boolean, _size: CategoryPillSize = "md"): CSSProperties {
  return CATEGORY_PILL_FRAME;
}

export const CATEGORY_PILL_CLASS =
  "block whitespace-nowrap transition-transform duration-200 hover:-translate-y-[1px]";

/**
 * The pill's contents. Rendered inside whatever link the surface needs — the
 * home rail and the hub rail point at different routes, so the anchor cannot
 * live in here.
 */
export function CategoryPillBody({
  name,
  thumbnail,
  active,
  size = "md",
}: {
  name: string;
  thumbnail?: string;
  active?: boolean;
  size?: CategoryPillSize;
}): ReactNode {
  const s = SIZES[size];
  return (
    <span className={`flex items-center ${s.pill}`} style={active ? INNER_ACTIVE : INNER}>
      <span
        className={`flex flex-shrink-0 items-center justify-center rounded-full ${s.disc}`}
        style={{ background: active ? "rgba(255,255,255,0.12)" : "rgba(11,15,77,0.05)" }}
      >
        {/* Plain <img>: the pill is a fixed size and a missing icon should leave
            a quiet empty disc, not a fallback graphic sized for a card. */}
        {thumbnail ? (
          <img src={thumbnail} alt="" loading="lazy" className={`${s.art} object-contain`} />
        ) : null}
      </span>
      <span className={`font-semibold ${s.text}`} style={{ color: active ? "#fff" : "#110E39" }}>
        {name}
      </span>
    </span>
  );
}
